import React, { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import ProductCard from "@/components/products/ProductCard";
import CTABanner from "@/components/site/CTABanner";

const slugify = (text) => (text || "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/(^-|-$)/g, "");

export default function Products() {
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);

  const loadData = async () => {
    try {
      let cats = await base44.entities.ProductCategory.list("name").catch(() => []);
      if (cats.length === 0) {
        const defaults = ["Building Expansion Joints", "Bridge Expansion Joints", "Seismic Joint Systems"];
        for (const name of defaults) {
          await base44.entities.ProductCategory.create({ name }).catch(() => {});
        }
        cats = await base44.entities.ProductCategory.list("name").catch(() => []);
      }
      setCategories(cats);
      
      const prods = await base44.entities.Product.filter({ status: "active" }).catch(() => []);
      setProducts(prods);
    } catch (err) {
      console.error("Failed to load products/categories:", err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (products && window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [products]);

  return (
    <>
      <section className="bg-[#0A0A0A] pt-40 pb-28 clip-diagonal-bottom relative overflow-hidden">
        <div className="absolute top-0 right-0 h-full w-40 bg-[#E4001C]/70 hidden md:block" style={{ clipPath: "polygon(60% 0, 100% 0, 40% 100%, 0 100%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <p className="font-tech font-semibold uppercase tracking-[0.25em] text-[#E4001C] text-sm mb-4">Product Systems</p>
          <h1 className="font-heading font-extrabold uppercase text-white text-5xl md:text-7xl tracking-wide max-w-3xl">
            The Performance Gallery
          </h1>
        </div>
      </section>

      <div className="sticky top-16 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-6 overflow-x-auto py-4">
          {categories.map((c) => {
            const anchor = slugify(c.name);
            return (
              <a key={c.id} href={`#${anchor}`}
                className="font-tech font-semibold uppercase tracking-widest text-sm whitespace-nowrap text-[#171717] hover:text-[#E4001C] transition-colors">
                {c.name}
              </a>
            );
          })}
        </div>
      </div>

      {!products ? (
        <div className="py-32 flex justify-center">
          <div className="w-8 h-8 border-4 border-gray-200 border-t-[#E4001C] rounded-full animate-spin" />
        </div>
      ) : (
        categories.map((c, i) => {
          const items = products.filter((p) => p.category === c.name);
          const anchor = slugify(c.name);
          return (
            <section key={c.id} id={anchor} className={`py-20 scroll-mt-32 ${i % 2 ? "bg-[#F5F5F5]" : "bg-white"}`}>
              <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="flex items-center gap-4 mb-10">
                  <div className="w-1.5 h-10 bg-[#E4001C]" />
                  <h2 className="font-heading font-extrabold uppercase text-3xl md:text-4xl tracking-wide text-[#171717]">{c.name}</h2>
                </div>
                {items.length === 0 ? (
                  <p className="text-[#A8ADB4] font-tech uppercase tracking-widest text-sm">Product lineup coming soon — contact us for current availability.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {items.map((p) => <ProductCard key={p.id} product={p} />)}
                  </div>
                )}
              </div>
            </section>
          );
        })
      )}
      <CTABanner />
    </>
  );
}